// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Button, Input } from '@imprivata-cloud/design-system';
import { Tooltip } from 'antd';
import { Trash2 } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './RedirectUrisField.less';

const URI_SCHEME_REGEX = /^[a-zA-Z][a-zA-Z0-9+\-.]*:/;

export interface RedirectUrisFieldTranslations {
  itemLabel: string;
  addLabel: string;
  removeLabel: string;
  requiredError: string;
  requiredErrorAnother: string;
  formatError: string;
  duplicateError: string;
}

export interface RedirectUrisFieldProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  onValidChange?: (isValid: boolean) => void;
  disabled?: boolean;
  minItems?: number;
  translations?: Partial<RedirectUrisFieldTranslations>;
}

const RedirectUrisField = ({
  value = [''],
  onChange = () => {},
  onValidChange,
  disabled,
  minItems = 1,
  translations,
}: RedirectUrisFieldProps) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<Record<number, string | null>>({});
  const focusedIndex = useRef<number | null>(null);
  const idPrefix = useId();

  const tKey = useCallback(
    (key: keyof RedirectUrisFieldTranslations, fallback: string) =>
      translations?.[key] ?? t(fallback),
    [translations, t],
  );

  const notifyValidity = useCallback(
    (nextErrors: Record<number, string | null>, nextValue: string[]) => {
      if (!onValidChange) {
        return;
      }
      const allClear = Object.values(nextErrors).every((e) => !e);
      const allFilled = nextValue.every((v) => v.trim() !== '');
      onValidChange(allClear && allFilled);
    },
    [onValidChange],
  );

  const validate = useCallback(
    (index: number, currentValue: string[]): string | null => {
      const v = currentValue[index];
      if (!v || v.trim() === '') {
        return currentValue.length > 1
          ? tKey('requiredErrorAnother', 'common.redirect-uris.required-another')
          : tKey('requiredError', 'common.redirect-uris.required');
      }
      if (!URI_SCHEME_REGEX.test(v)) {
        return tKey('formatError', 'common.redirect-uris.invalid');
      }
      const isDuplicate = currentValue.some((other, i) => i !== index && other === v);
      if (isDuplicate) {
        return tKey('duplicateError', 'common.redirect-uris.duplicate');
      }
      return null;
    },
    [tKey],
  );

  const handleBlur = useCallback(
    (index: number) => {
      focusedIndex.current = null;
      const error = validate(index, value);
      setErrors((prev) => {
        const next = { ...prev, [index]: error };
        notifyValidity(next, value);
        return next;
      });
    },
    [validate, value, notifyValidity],
  );

  const handleDelete = useCallback(
    (index: number) => {
      const nextValue = value.filter((_, i) => i !== index);
      setErrors((prev) => {
        const next: Record<number, string | null> = {};
        for (const [k, v] of Object.entries(prev)) {
          const i = Number(k);
          if (i < index) {
            next[i] = v;
          } else if (i > index) {
            next[i - 1] = v;
          }
        }
        notifyValidity(next, nextValue);
        return next;
      });
      onChange(nextValue);
    },
    [value, onChange, notifyValidity],
  );

  // Sync errors when value array length shrinks externally
  useEffect(() => {
    setErrors((prev) => {
      const next: Record<number, string | null> = {};
      for (const [k, v] of Object.entries(prev)) {
        const i = Number(k);
        if (i < value.length) {
          next[i] = v;
        }
      }
      return next;
    });
  }, [value.length]);

  // Report validity on every value change so the consumer's canSave gate stays
  // accurate without waiting for a blur event. Does not update displayed errors.
  useEffect(() => {
    const silentErrors: Record<number, string | null> = {};
    for (let i = 0; i < value.length; i++) {
      silentErrors[i] = validate(i, value);
    }
    notifyValidity(silentErrors, value);
  }, [value, validate, notifyValidity]);

  const itemLabel = tKey('itemLabel', 'common.redirect-uris.label');
  const addLabel = tKey('addLabel', 'common.redirect-uris.add');
  const removeLabel = tKey('removeLabel', 'common.redirect-uris.remove-aria-label');

  return (
    <div className="redirect-uris-field">
      {value.map((uri, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: URIs can be duplicates during editing; no stable identity exists until committed
        <div key={index} className="redirect-uris-item">
          <label className="redirect-uris-label" htmlFor={`${idPrefix}-${index}`}>
            {itemLabel}
          </label>
          <div className="redirect-uris-row">
            <div className="redirect-uris-input-wrap">
              <Input
                id={`${idPrefix}-${index}`}
                value={uri}
                placeholder="https://example.com/callback"
                disabled={disabled}
                error={!!errors[index]}
                errorMessage={errors[index] ?? undefined}
                onFocus={() => {
                  focusedIndex.current = index;
                }}
                onBlur={() => handleBlur(index)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const next = value.map((v, i) => (i === index ? e.target.value : v));
                  onChange(next);
                }}
              />
            </div>
            {value.length > minItems && (
              <Tooltip title={t('common.redirect-uris.remove')}>
                <Button
                  type="button"
                  aria-label={`${removeLabel} ${index + 1}`}
                  disabled={disabled}
                  onClick={() => handleDelete(index)}
                  className="redirect-uris-delete"
                >
                  <Trash2 size={24} />
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="link"
        className="redirect-uris-add"
        disabled={disabled}
        onClick={() => onChange([...value, ''])}
      >
        <span>{addLabel}</span>
      </Button>
    </div>
  );
};

export default RedirectUrisField;
